import {
  type BlockContent,
  type Call,
  callsByRun,
  editableRuns,
  type Inline,
  type NodeContent,
  runDirection,
  runsOf,
  writeRun,
} from '@app/studio'
import { MessageSquare, MessageSquarePlus } from 'lucide-react'
import { Fragment, useContext, useMemo } from 'react'
import {
  commitRepresentableContent,
  RunEditor,
  useContentRefusal,
} from '../../ui/RunEditor'
import { InlineRun } from './InlineRun'
import {
  type Block,
  Book,
  Comments,
  type Section,
  Writing,
} from './reader-context'

export function ReaderSection({ section }: { section: Section }) {
  const Heading = `h${Math.min(section.depth + 2, 6)}` as 'h2'
  const writing = useContext(Writing)
  const open = writing.openSectionId === section.id
  const armed = writing.armed && !open && !!writing.onOpenSection
  const { refusing: refusingContent, markRefusal } = useContentRefusal()

  /* A title being written is drawn from the DRAFT, so what is on screen is what
     would be saved. */
  const title =
    open && writing.sectionDraft ? writing.sectionDraft.title : section.title
  /* A subtitle is a rank of its own under the title — the line saying what a
     part is about, which the title states only by name. */
  const subtitle =
    open && writing.sectionDraft
      ? writing.sectionDraft.subtitle
      : section.subtitle

  /* A heading is text and may call a note, so its title and its subtitle are one
     sequence of calls in that order — the same slicing a passage's runs get. */
  const headingCalls = useMemo(() => {
    const said = {
      kind: section.kind,
      title,
      ...(subtitle ? { subtitle } : {}),
    } as NodeContent
    const per = callsByRun(said, section.calls ?? [])
    return new Map(runsOf(said).map((r, i) => [r.path, per[i] ?? []]))
  }, [section.kind, section.calls, title, subtitle])

  const draftOf = (
    next: Partial<{
      title: Inline[]
      subtitle: Inline[] | null
      calls: Call[]
    }>,
  ) =>
    writing.onSectionDraft?.({
      title: next.title ?? title,
      subtitle:
        next.subtitle !== undefined ? next.subtitle : (subtitle ?? null),
      calls: next.calls ?? section.calls ?? [],
    })

  /* Opening a heading is the same gesture as opening a passage, so it carries
     the same attributes and the same keyboard way in. */
  const opener = armed
    ? {
        'data-writable': '',
        onClick: () => writing.onOpenSection?.(section.id),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            writing.onOpenSection?.(section.id)
          }
        },
        role: 'button',
        tabIndex: 0,
      }
    : {}

  return (
    /* `data-passage` marks a place the contents can point at — the same
       attribute a source's segments carry, so one rail finds its targets in
       either. A run-in heading listed in the contents wears it too, on the
       block that holds it. */
    <section
      className={`reader__section book-section book-section--${section.kind}`}
      data-passage={section.id}
    >
      {/*
        A heading is text somebody wrote, so it is edited where it is read — in
        its own face and rank, like a passage.

        Drawn as the section's OWN children rather than inside a group: the rule
        that leaves a first paragraph unindented tests what stands immediately
        before it, so a wrapper here silently indents the opening paragraph of
        every section in the book.
      */}
      {open ? (
        <RunEditor
          className="book-section__title"
          value={title}
          autoFocus
          onCommit={() =>
            commitRepresentableContent(refusingContent, writing.onCommit)
          }
          onCancel={writing.onCancel}
          onContentRefusalChange={(refused) => markRefusal('title', refused)}
          calls={headingCalls.get('title')}
          onValueChange={(run, runCalls) =>
            draftOf({
              title: run,
              /* The title's calls come first, then whatever the subtitle
                 makes — one sequence over the heading, in reading order. */
              calls: [...runCalls, ...(headingCalls.get('subtitle') ?? [])],
            })
          }
        />
      ) : (
        <Heading className="book-section__title" {...opener}>
          <InlineRun run={title} calls={headingCalls.get('title')} />
        </Heading>
      )}
      {open ? (
        /* Offered whether or not the part has one: a subtitle a section has
           never had is exactly what somebody opening the heading may want to
           write, and one appearing only where it already existed could never be
           added. */
        <RunEditor
          className="book-section__subtitle"
          value={subtitle ?? []}
          placeholder="A line under the title, if this part wants one"
          onCommit={() =>
            commitRepresentableContent(refusingContent, writing.onCommit)
          }
          onCancel={writing.onCancel}
          onContentRefusalChange={(refused) => markRefusal('subtitle', refused)}
          calls={headingCalls.get('subtitle')}
          onValueChange={(run, runCalls) =>
            draftOf({
              subtitle: run.length ? run : null,
              calls: [...(headingCalls.get('title') ?? []), ...runCalls],
            })
          }
        />
      ) : subtitle?.length ? (
        <p className="book-section__subtitle" {...opener}>
          <InlineRun run={subtitle} calls={headingCalls.get('subtitle')} />
        </p>
      ) : null}
      {open && !refusingContent && writing.sectionCommit
        ? writing.sectionCommit
        : null}
      {section.blocks.map((b, i) => (
        <ReaderBlock key={b.id} block={b} first={i === 0} />
      ))}
      {section.children.map((c) => (
        <ReaderSection key={c.id} section={c} />
      ))}
    </section>
  )
}

/**
 * One block, addressable.
 *
 * A comment is about a passage, so every block carries its own id in the
 * markup: that is what lets the band scroll the reader to the paragraph a
 * thread is about, and the paragraph send the reader to the thread.
 */
function ReaderBlock({ block, first }: { block: Block; first?: boolean }) {
  const { counts, onOpen, canRaise, revealed, picked } = useContext(Comments)
  const open = counts.get(block.id) ?? 0
  /* Words are held in this passage, so the mark carries them rather than the
     paragraph — and it shows whether or not the passage has been remarked on,
     because selecting is somebody saying what they want to talk about. */
  const holding = picked === block.id
  /* A divider is not a passage — there is nothing in it to raise a point about. */
  const marked =
    !!onOpen &&
    block.content.kind !== 'divider' &&
    (open > 0 || holding || !!canRaise)

  const writing = useContext(Writing)
  const writingHere = writing.openId === block.id
  const { refusing: refusingContent, markRefusal } = useContentRefusal()
  /* A divider holds no words, so there is nothing in it to open for writing —
     the same reason it carries no comment mark. */
  const writable =
    writing.armed &&
    !writingHere &&
    block.content.kind !== 'divider' &&
    !!writing.onOpen

  return (
    <div
      className="reader__block"
      data-block={block.id}
      {...(first ? { 'data-first': '' } : {})}
      {...(block.content.kind === 'run_in_heading'
        ? { 'data-passage': block.id }
        : {})}
      {...(revealed?.id === block.id ? { 'data-revealed': '' } : {})}
      {...(writable ? { 'data-writable': '' } : {})}
      {...(writingHere ? { 'data-writing': '' } : {})}
      /* Armed, a click on the passage opens it for writing. Unarmed this
         listener is not attached at all, so selecting words to remark on is
         untouched — the two acts share a gesture and only one of them may have
         it at a time. */
      {...(writable
        ? {
            onClick: () => writing.onOpen?.(block.id),
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                writing.onOpen?.(block.id)
              }
            },
            role: 'button',
            tabIndex: 0,
          }
        : {})}
    >
      <BlockBody
        block={block}
        refusingContent={refusingContent}
        onContentRefusalChange={markRefusal}
      />
      {/* Directly under the passage it commits, so what is being saved is
          never in doubt and the controls are where the eye already is. */}
      {writingHere && !refusingContent && writing.commit
        ? writing.commit
        : null}
      {marked && onOpen ? (
        <button
          type="button"
          className="reader__comment"
          data-anchor-skip
          {...(holding ? { 'data-holding': '' } : {})}
          aria-label={
            holding
              ? 'Comment on the selected words'
              : open
                ? `Read the ${open} open ${open === 1 ? 'comment' : 'comments'} on this passage`
                : 'Comment here'
          }
          /*
           * The mark does what its own icon promised.
           *
           * A count says a discussion is here, so it takes the reader to it and
           * writes nothing; a plus says there is somewhere to write, so it opens
           * the box. Held words are always a plus — selecting is somebody saying
           * what they want to talk about, even on a passage already discussed.
           *
           * The pointer is already up by the time this fires, so the selection
           * has been read and held: pressing this cannot lose it.
           */
          onClick={() =>
            onOpen(block.id, !holding && open > 0 ? 'read' : 'raise')
          }
        >
          {open && !holding ? (
            <>
              <MessageSquare size={13} />
              <span className="reader__commentn">{open}</span>
            </>
          ) : (
            <MessageSquarePlus size={13} />
          )}
        </button>
      ) : null}
    </div>
  )
}

/**
 * What an unnamed speaker slot says while it stands empty.
 *
 * It states the meaning of leaving it alone rather than instructing anybody to
 * fill it in, because the absence is a legitimate answer and the commonest one:
 * a speech resuming after a line set apart names nobody.
 */
const SPEAKER_PLACEHOLDER =
  'Who is speaking — empty means the speaker before carries on'

function BlockBody({
  block,
  refusingContent,
  onContentRefusalChange,
}: {
  block: Block
  refusingContent: boolean
  onContentRefusalChange: (path: string, refused: boolean) => void
}) {
  const book = useContext(Book)
  const writing = useContext(Writing)
  /*
   * The passage being written is drawn from the DRAFT, so what is on screen is
   * what would be saved. Everything else reads from the block as stored.
   */
  const open = writing.openId === block.id
  const c = open && writing.draft ? writing.draft.content : block.content
  const heldCalls = open && writing.draft ? writing.draft.calls : block.calls

  /*
   * What each of this passage's runs calls, keyed by where the run sits.
   *
   * A marker says only that a call is here; WHICH call it is, is its position
   * among them. Sliced per run rather than counted as the tree is drawn, because
   * a component renders more than once and a counter advanced during render
   * would resolve the same marker differently the second time.
   */
  const callsAt = useMemo(() => {
    const said = c as NodeContent
    const per = callsByRun(said, heldCalls ?? [])
    return new Map(runsOf(said).map((r, i) => [r.path, per[i] ?? []]))
  }, [c, heldCalls])

  /*
   * The one place reading becomes writing.
   *
   * The editor is drawn AS the book's own element, carrying the same class —
   * not inside it. Nested, the words sit in a second block box and the
   * paragraph's indent and spacing apply twice, so the text shifts the moment a
   * passage is opened. One box either way is what keeps it still.
   */
  const line = (
    Tag: 'p' | 'li' | 'cite' | 'figcaption',
    className: string | undefined,
    run: Inline[],
    path: string,
    /* `placeholder` reaches the writing surface only. It says what an empty run
       is FOR, and a page being read holds no empty run to say it of. */
    opts?: { lang?: string; dir?: string; placeholder?: string },
  ) => {
    const { placeholder, ...attrs } = opts ?? {}
    return open && writing.onDraft ? (
      <RunEditor
        className={className}
        value={run}
        autoFocus={path === editableRuns(c)[0]?.path}
        onCommit={() =>
          commitRepresentableContent(refusingContent, writing.onCommit)
        }
        onCancel={writing.onCancel}
        onContentRefusalChange={(refused) =>
          onContentRefusalChange(path, refused)
        }
        calls={callsAt.get(path)}
        {...(placeholder ? { placeholder } : {})}
        onValueChange={(next, nextCalls) => {
          const written = writeRun(c, heldCalls ?? [], path, next, nextCalls)
          writing.onDraft?.({
            content: written.content as BlockContent,
            calls: written.calls,
          })
        }}
        {...attrs}
      />
    ) : (
      <Tag className={className} {...attrs}>
        <InlineRun run={run} calls={callsAt.get(path)} />
      </Tag>
    )
  }

  /* A verse and a source quote are a pair of lines in two known languages, so
     each states its own side rather than inheriting the book's — which keeps
     the translation running the right way inside a Hebrew book. */
  const heDir = runDirection(book, 'he')
  const enDir = runDirection(book, 'en')

  switch (c.kind) {
    case 'paragraph':
      return line(
        'p',
        `book-p${c.lead ? ' book-p--lead' : ''}`,
        c.content,
        'content',
      )

    case 'claim_list':
      // Each claim is its own line, because each is its own proposition.
      return (
        <ul className="book-claims">
          {c.items.map((item, i) => (
            <li className="book-claim" key={`${block.id}-${i}`}>
              {open && writing.onDraft ? (
                line('li', undefined, item, `items.${i}`)
              ) : (
                <InlineRun run={item} calls={callsAt.get(`items.${i}`)} />
              )}
            </li>
          ))}
        </ul>
      )

    /* Reference, Hebrew, translation and classification are one block in the
       author's order, so what a passage is and where it comes from cannot be
       separated. */
    case 'verse':
      return (
        <div className={`book-verse book-verse--${c.display}`}>
          {/* A reference names no language of its own — "Genesis 4:26" and
              "ברכת התורה" are both references — so the line takes its direction
              from its first strong character rather than from a field. */}
          {c.reference
            ? line('p', 'book-verse__ref', c.reference, 'reference', {
                dir: 'auto',
              })
            : null}
          {line('p', 'book-verse__he', c.he, 'he', {
            lang: 'he',
            ...(heDir ? { dir: heDir } : {}),
          })}
          {c.en
            ? line('p', 'book-verse__en', c.en, 'en', {
                ...(enDir ? { dir: enDir } : {}),
              })
            : null}
          {c.classification ? (
            <p className="book-verse__class">{c.classification}</p>
          ) : null}
        </div>
      )

    case 'source_quote':
      return (
        <blockquote className="book-quote">
          {line('p', 'book-quote__he', c.he, 'he', {
            lang: 'he',
            ...(heDir ? { dir: heDir } : {}),
          })}
          {line('p', 'book-quote__en', c.en, 'en', {
            ...(enDir ? { dir: enDir } : {}),
          })}
          {c.attribution
            ? line('cite', 'book-quote__cite', c.attribution, 'attribution')
            : null}
        </blockquote>
      )

    case 'quotation':
      return (
        <blockquote className="book-quotation">
          {/*
            An attribution with nothing quoted above it is the source line of
            the block before, so an empty body prints no paragraph rather than
            a blank line the page cannot account for.

            Open for writing, the slot stands whether or not it holds words: a
            body selected and deleted would otherwise take its own editor out
            from under the caret mid-keystroke.
          */}
          {c.content.length || (open && writing.onDraft)
            ? line('p', undefined, c.content, 'content')
            : null}
          {c.attribution
            ? line('cite', undefined, c.attribution, 'attribution')
            : null}
        </blockquote>
      )

    case 'run_in_heading':
      return line('p', 'book-heading', c.content, 'content')

    case 'dialogue':
      return (
        <div className="book-dialogue">
          {c.turns.map((t, i) => (
            <div className="book-turn" key={`${block.id}-${i}`}>
              {/*
                A turn naming nobody is the speaker before carrying on — a
                speech resuming after a line set apart. No label, rather than an
                empty one where a label goes.

                Open for writing, the slot stands whether or not the turn names
                anybody. A stored turn that names nobody offers nothing on the
                page to click, so without the slot the absence is a statement
                only the tool bridge can take back.
              */}
              {t.speaker || (open && writing.onDraft)
                ? line(
                    'p',
                    'book-turn__speaker',
                    t.speaker ?? [],
                    `turns.${i}.speaker`,
                    { placeholder: SPEAKER_PLACEHOLDER },
                  )
                : null}
              {t.content.map((run, j) => (
                <Fragment key={`${block.id}-${i}-${j}`}>
                  {line('p', undefined, run, `turns.${i}.content.${j}`)}
                </Fragment>
              ))}
            </div>
          ))}
        </div>
      )

    case 'divider':
      return <hr className={`book-divider book-divider--${c.variant}`} />

    case 'figure':
      return (
        <figure className="book-figure">
          {c.caption
            ? line('figcaption', undefined, c.caption, 'caption')
            : null}
        </figure>
      )

    default:
      return null
  }
}
